import * as contracts from './contracts';
export function createHub({ window: win, document: doc, fetch: fetchApi, config: inputConfig }) {
    const element = (id) => doc.getElementById(id);
    const on = (target, event, handler) => {
      target.addEventListener(event, handler);
      listeners.push(() => target.removeEventListener(event, handler));
    };
    const listeners = [];
    const buttons = new Map();
    const lines = [];
    let config;
    let token = '';
    let sessionProfile = null;
    let generation = 0;
    let request = null;
    let activeFrame = null;
    let selectedScreen = '';
    let activeTab = null;
    let receivedHeight = 0;
    let pendingJump = null;
    let jumpFrame = null;
    let readyTimer = null;
    let disposed = false;
    const status = (text, error = false) => {
      element('status').textContent = text;
      element('status').dataset.error = String(error);
    };
    try {
      config = contracts.validateConfig(inputConfig, win.location.href);
    } catch (error) {
      // Configuration errors are fixed text from validation; never request data.
      status(error instanceof Error ? error.message : 'Invalid QA configuration.', true);
      return { dispose() {} };
    }
    const profile = () => config.profiles.find((item) => item.id === element('profile').value);
    const context = () => Object.fromEntries(['ric', 'ticker', 'mkt', 'locale', 'theme'].map((key) => [key, element(key).value]));
    const clearReady = () => { win.clearTimeout(readyTimer); readyTimer = null; };
    const clearJump = () => {
      pendingJump = null;
      if (jumpFrame !== null) win.cancelAnimationFrame(jumpFrame);
      jumpFrame = null;
    };
    const clearPreview = () => {
      clearReady(); clearJump();
      if (activeFrame) activeFrame.remove();
      activeFrame = null;
      selectedScreen = '';
      activeTab = null;
      receivedHeight = 0;
      lines.length = 0;
      element('messages').textContent = '';
      element('reload').disabled = true;
      element('back').disabled = true;
      element('viewerTitle').textContent = 'Widget preview';
      element('frameStatus').textContent = 'Choose a screen from the catalog.';
      for (const button of buttons.values()) button.removeAttribute('aria-current');
    };
    const setBusy = (busy) => {
      for (const id of ['username', 'password', 'profile', 'signIn']) element(id).disabled = busy;
      element('cancelLogin').hidden = !busy;
      element('signIn').textContent = busy ? 'Signing in…' : 'Sign in';
    };
    function logout() {
      generation++;
      if (request) request.cancel();
      request = null;
      token = '';
      sessionProfile = null;
      element('password').value = '';
      clearPreview(); setBusy(false);
      element('workspace').hidden = true;
      element('loginPanel').hidden = false;
      element('logout').hidden = true;
      status('Sign in with your test API account.');
    }
    const authError = (code) => ({
      rejected: 'Sign-in failed. Check your username and password.',
      timeout: 'Sign-in timed out. Please retry.',
      invalid: 'The login API returned no valid token. Please retry.',
      server: 'The login API could not complete sign-in. Please retry.',
      network: 'Cannot reach the login API. Check connectivity and CORS for this QA origin.',
    }[code] || 'Sign-in could not be completed. Please retry.');
    async function login() {
      if (disposed || request || token) return;
      const username = element('username').value.trim();
      const password = element('password').value;
      if (!username || !password) { status('Enter username and password.', true); return; }
      const selectedProfile = profile();
      if (!selectedProfile) { status('Choose a configured test environment.', true); return; }
      const revision = ++generation;
      const controller = new AbortController();
      let timer;
      let rejectControl;
      const control = new Promise((_, reject) => { rejectControl = reject; });
      const attempt = {
        cancel() { win.clearTimeout(timer); rejectControl({ code: 'cancelled' }); controller.abort(); },
      };
      request = attempt;
      setBusy(true);
      status('Signing in…');
      element('password').value = '';
      timer = win.setTimeout(() => {
        rejectControl({ code: 'timeout' });
        controller.abort();
      }, config.loginTimeoutMs);
      try {
        const operation = (async () => {
          const response = await fetchApi(selectedProfile.loginUrl, {
            method: 'POST', headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
            body: JSON.stringify({ username, password }), signal: controller.signal,
            credentials: 'omit', redirect: 'error', cache: 'no-store', referrerPolicy: 'strict-origin',
          });
          if (!response.ok) throw { code: response.status === 401 ? 'rejected' : 'server' };
          let body;
          try { body = await response.json(); } catch (_) { throw { code: 'invalid' }; }
          if (!body || typeof body.token !== 'string' || !contracts.normalize(body.token)) throw { code: 'invalid' };
          return contracts.normalize(body.token);
        })();
        const result = await Promise.race([operation, control]);
        if (disposed || revision !== generation) return;
        token = result;
        sessionProfile = selectedProfile;
        element('loginPanel').hidden = true;
        element('workspace').hidden = false;
        element('logout').hidden = false;
        element('environment').textContent = selectedProfile.label;
        status('Signed in. Choose a screen to load.');
        updateAvailability();
      } catch (error) {
        if (!disposed && revision === generation) status(authError(error && error.code || 'network'), true);
      } finally {
        win.clearTimeout(timer);
        if (revision === generation) { request = null; setBusy(false); }
      }
    }
    function updateAvailability() {
      for (const entry of contracts.CATALOG) {
        const button = buttons.get(entry.id);
        button.disabled = !contracts.available(entry, element('mkt').value);
        button.title = button.disabled ? 'Unavailable for US instruments' : '';
      }
    }
    const appendMessage = (message) => {
      lines.push(JSON.stringify(message));
      if (lines.length > 40) lines.shift();
      element('messages').textContent = lines.join('\n');
    };
    const setTab = (tab) => {
      if (activeTab !== tab) clearJump();
      activeTab = tab;
    };
    const scheduleJump = () => {
      if (!pendingJump || !activeFrame || !receivedHeight || activeTab !== pendingJump.tab_id || jumpFrame !== null) return;
      const frame = activeFrame;
      jumpFrame = win.requestAnimationFrame(() => {
        jumpFrame = null;
        if (frame !== activeFrame || !pendingJump || activeTab !== pendingJump.tab_id || pendingJump.offsetTop > receivedHeight) return;
        const offset = Number(element('headerOffset').value);
        const headerOffset = Number.isFinite(offset) && offset >= 0 && offset <= 2000 ? offset : 0;
        const top = frame.getBoundingClientRect().top + win.scrollY + frame.clientTop + pendingJump.offsetTop - headerOffset;
        pendingJump = null;
        if (Number.isFinite(top)) win.scrollTo({ top: Math.max(0, top), behavior: win.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
      });
    };
    function handleMessage(event) {
      if (!activeFrame || !token || event.source !== activeFrame.contentWindow ||
          event.origin !== new URL(sessionProfile.widgetBase).origin || contracts.getScreen(selectedScreen).route !== 'analysis') return;
      const message = contracts.parseMessage(event.data);
      if (!message) return;
      if (message.action === 'heightchange') {
        setTab(message.tab_id);
        activeFrame.style.height = `${message.height}px`;
        receivedHeight = message.height;
        clearReady();
        element('frameStatus').textContent = `Height received · tab ${message.tab_id} · ${message.height}px. Check data in the widget.`;
        scheduleJump();
      } else if (message.type === 'asp_tab_change') {
        setTab(message.tab_id);
      } else {
        if (activeTab !== message.tab_id) return;
        pendingJump = message;
        scheduleJump();
      }
      appendMessage(message);
    }
    function loadScreen(id) {
      if (disposed || !token) return;
      const screen = contracts.getScreen(id);
      let url;
      try { url = contracts.buildUrl(config, sessionProfile.id, id, context(), token); }
      catch (error) { status(error.message, true); return; }
      if (screen.route === 'login') {
        win.open(url.href, '_blank', 'noopener,noreferrer');
        status('Original widget login opened separately. The QA Hub token was not passed.');
        return;
      }
      clearPreview();
      selectedScreen = id;
      activeTab = Number(screen.tab);
      const frame = doc.createElement('iframe');
      frame.title = `eSmart ${screen.group} — ${screen.label}`;
      frame.referrerPolicy = 'strict-origin';
      frame.style.height = screen.route === 'indices' ? '720px' : '640px';
      // Attach handlers before navigation. No access to cross-origin child DOM.
      frame.addEventListener('load', () => {
        if (frame !== activeFrame) return;
        clearJump();
        if (screen.route === 'indices') {
          clearReady();
          element('frameStatus').textContent = 'Indices document loaded. Check login and data in the widget; no Analysis height message is expected.';
        } else if (!receivedHeight) {
          element('frameStatus').textContent = 'Document loaded. Waiting for the widget’s settled height; this does not confirm API success.';
        }
      });
      activeFrame = frame;
      element('viewerTitle').textContent = `${screen.group} · ${screen.label}`;
      element('frameStatus').textContent = 'Loading widget document…';
      element('reload').disabled = false;
      element('back').disabled = false;
      buttons.get(id).setAttribute('aria-current', 'page');
      readyTimer = win.setTimeout(() => {
        if (activeFrame === frame) element('frameStatus').textContent = 'Connection pending. Check the widget, network, embedding permissions and parent-origin configuration. Re-sign in manually if needed.';
      }, config.connectionTimeoutMs);
      frame.src = url.href;
      element('frameMount').appendChild(frame);
      status('Preview opened. Context edits apply the next time you select a screen or Reload.');
    }
    for (const item of config.profiles) {
      const option = doc.createElement('option');
      option.value = item.id; option.textContent = item.label;
      element('profile').appendChild(option);
    }
    element('profile').value = config.profiles[0].id;
    let lastGroup = '';
    for (const entry of contracts.CATALOG) {
      if (entry.group !== lastGroup) {
        const heading = doc.createElement('h3'); heading.textContent = entry.group;
        element('catalog').appendChild(heading); lastGroup = entry.group;
      }
      const button = doc.createElement('button'); button.type = 'button'; button.textContent = entry.label;
      on(button, 'click', () => loadScreen(entry.id));
      element('catalog').appendChild(button); buttons.set(entry.id, button);
    }
    on(element('loginForm'), 'submit', (event) => { event.preventDefault(); void login(); });
    on(element('cancelLogin'), 'click', logout);
    on(element('logout'), 'click', logout);
    on(element('back'), 'click', clearPreview);
    on(element('reload'), 'click', () => loadScreen(selectedScreen));
    on(element('mkt'), 'change', updateAvailability);
    for (const [id, ticker, market] of [['presetJp', '6269', 'JP'], ['presetUs', 'AAPL', 'US']]) {
      on(element(id), 'click', () => { element('ticker').value = ticker; element('mkt').value = market; updateAvailability(); });
    }
    const updateWidth = () => {
      const width = element('width').value;
      if (['626', '600', '599', '375', '360', 'fluid'].includes(width)) element('preview').style.width = width === 'fluid' ? '100%' : `${width}px`;
    };
    on(element('width'), 'change', updateWidth);
    on(win, 'message', handleMessage);
    on(win, 'pagehide', logout);
    updateWidth(); updateAvailability(); logout();
    return {
      login, logout, loadScreen,
      dispose() {
        logout(); disposed = true; listeners.forEach((remove) => remove());
        // React StrictMode replays effects without recreating the host nodes.
        element('profile').replaceChildren();
        element('catalog').replaceChildren();
      },
    };
  }
